import C from "@/components/sidebar/StandardSidebar.module.css";

import { BadgeWithTip } from "@/components/Badge.tsx";
import { addressCompareBy, localeCompareBy } from "@/components/data/comparers.ts";
import { getFilteredItems } from "@/components/data/getFilteredItems.ts";
import { getSortedItems, type SimpleSortField } from "@/components/data/getSortedItems.ts";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { EmptyItem } from "@/components/sidebar/EmptyItem.tsx";
import { PriceBlock } from "@/components/sidebar/PriceBlock.tsx";
import { ListResult, SidebarList, type SidebarListState } from "@/components/sidebar/SidebarList.tsx";
import { StandardBlock } from "@/components/sidebar/StandardSidebar.tsx";
import { networkCreateLink } from "@/modules/api/resourceCreateLinks.ts";
import type { FirewallWithType } from "@/modules/network/firewall/firewallApi.ts";
import { UNKNOWN_RESOURCE } from "@/modules/network/genericResources.ts";
import type { IpAddressWithPrice, LoadBalancerWithPrice } from "@/modules/network/ipAddress/joinAssignedQuery.ts";
import { getDisplayName, UNASSIGNED_RESOURCE } from "@/modules/network/ipAddress/resourceId.ts";
import { firewallLink, ipAddressLink, loadBalancerLink, vpcLink } from "@/modules/network/links.ts";
import { type NetworkListItem, useNetworkListQuery } from "@/modules/network/networkListQuery.ts";
import type { VpcWithType } from "@/modules/network/vpc/vpcApi.ts";
import { getResourceName } from "@/modules/resource/getResourceName.ts";
import { cn } from "@/utils/classNames.ts";
import { LOADING } from "@/utils/loading.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { datetimeToDate } from "@warrenio/api-spec/conversion";
import type { ReactNode } from "react";

const sortFields = [
    {
        id: "address",
        title: <Trans>Address</Trans>,
        compare: addressCompareBy((i) =>
            i.$type === "vpc"
                ? i.subnet
                : i.$type === "ip_address"
                  ? i.address
                  : i.$type === "firewall"
                    ? ""
                    : i.private_address,
        ),
    },
    { id: "created_at", title: <Trans>Created date</Trans>, getValue: (i) => datetimeToDate(i.created_at).getTime() },
    { id: "location", title: <Trans>Location</Trans>, getValue: (i) => i.location },
    { id: "type", title: <Trans>Type</Trans>, getValue: (i) => i.$type },
    {
        id: "name",
        title: <Trans>Name</Trans>,
        compare: localeCompareBy((i) => getResourceName(i)),
    },
] satisfies SimpleSortField<NetworkListItem>[];

export default function NetworkList() {
    const { t } = useLingui();
    return (
        <SidebarList
            data={(props) => <NetworkData {...props} />}
            title={t`Network`}
            sortFields={sortFields}
            actionButton={{
                title: t`Create new`,
                titleShort: t`New`,
                action: networkCreateLink,
            }}
            listId="network"
        />
    );
}

export function NetworkData({ queryString, sortId, sortAsc }: SidebarListState) {
    const query = useNetworkListQuery();

    let networkItems = getFilteredItems(queryString, query.data, (item) => {
        switch (item.$type) {
            case "vpc":
                return [item.name, item.subnet, item.uuid];
            case "ip_address":
                return [item.name, item.address, item.uuid];
            case "load_balancer":
                return [item.display_name, item.private_address, item.uuid];
            case "firewall":
                return [item.display_name, item.uuid];
        }
    });
    networkItems = getSortedItems(sortFields, { sortId, sortAsc }, networkItems);

    return (
        <ListResult
            items={networkItems.map((i) =>
                i.$type === "ip_address" ? (
                    <AddressBlock key={i.uuid} item={i} />
                ) : i.$type === "vpc" ? (
                    <VpcBlock key={i.uuid} item={i} />
                ) : i.$type === "firewall" ? (
                    <FirewallBlock key={i.uuid} item={i} />
                ) : (
                    <BalancerBlock key={i.uuid} item={i} />
                ),
            )}
            isSearching={!!queryString}
            query={query}
            emptyItem={
                <EmptyItem icon="jp-access-icon">
                    <Trans>Create a new resource and start managing your network.</Trans>
                </EmptyItem>
            }
        />
    );
}

function getAssignedName(ip: IpAddressWithPrice | undefined) {
    const assignedResource = ip?.assignedResource;
    switch (assignedResource) {
        case undefined: // IP address not loaded
        case LOADING:
            return "...";
        case UNKNOWN_RESOURCE:
            return <Trans>Unknown</Trans>;
        case UNASSIGNED_RESOURCE:
            return null;
        default:
            return getDisplayName(assignedResource);
    }
}

export interface AddressBlockProps {
    item: IpAddressWithPrice;
}

function AddressBlock({ item }: AddressBlockProps) {
    const { name, address, location, assigned_to_private_ip, billing_account_id } = item;
    const assigned_name = getAssignedName(item);

    return (
        <StandardBlock link={ipAddressLink(item)}>
            <div className={C.StandardBlockIcon}>
                <MaskIcon className="jp-network-2-icon size-1rem" />
            </div>

            <NamePiece name={name ? name : <i>{assigned_name}</i>} address={address} location={location} />

            <div className={C.StandardBlockType}>
                {assigned_to_private_ip ? (
                    <div className="text-success text-uppercase">
                        <Trans comment="State of IP address, attached to a resource such as a virtual machine">
                            Assigned
                        </Trans>
                    </div>
                ) : (
                    <div className="text-error text-uppercase">
                        <Trans comment="State of IP address">Unassigned</Trans>
                    </div>
                )}
            </div>

            <PriceBlock price={item.price} billingAccountId={billing_account_id} />
        </StandardBlock>
    );
}

export function VpcBlock({ item }: { item: VpcWithType }) {
    const { t } = useLingui();
    const { name, resources_count, subnet, location, is_default } = item;
    return (
        <StandardBlock link={vpcLink(item)}>
            <div className={C.StandardBlockIcon}>
                <MaskIcon className="jp-network-icon size-1rem" />
            </div>

            <NamePiece name={name} address={subnet} location={location} />

            <div className={C.StandardBlockInfo}>
                <div className="text-muted">
                    <Trans>No. of resources</Trans>
                </div>
                {resources_count ? resources_count : "0"}
            </div>

            {is_default && (
                <div className={C.holder}>
                    <BadgeWithTip description={t`Default`} noDot iconOnly inList>
                        <MaskIcon className="jp-icon-checkmark size-0.75rem" />
                    </BadgeWithTip>
                </div>
            )}
        </StandardBlock>
    );
}

export interface BalancerBlockProps {
    item: LoadBalancerWithPrice;
}

function BalancerBlock({ item }: BalancerBlockProps) {
    const { display_name, private_address, location, assignedPublicIp, billing_account_id } = item;
    const ipAddress = assignedPublicIp?.address;
    return (
        <StandardBlock link={loadBalancerLink(item)}>
            <div className={C.StandardBlockIcon}>
                <MaskIcon className="jp-load-balancer-icon size-1rem" />
            </div>

            <NamePiece name={display_name} address={ipAddress ?? private_address} location={location} />

            <PriceBlock price={item.price} billingAccountId={billing_account_id} />
        </StandardBlock>
    );
}

export interface FirewallBlockProps {
    item: FirewallWithType;
}

function FirewallBlock({ item }: FirewallBlockProps) {
    const { display_name, location, rules, resources_assigned } = item;

    const inbound = rules.filter((r) => r.direction === "inbound");
    const outbound = rules.filter((r) => r.direction === "outbound");

    return (
        <StandardBlock link={firewallLink(item)}>
            <div className={C.StandardBlockIcon}>
                <MaskIcon className="i-lucide:shield-ban size-1rem" />
            </div>

            <NamePiece
                name={display_name}
                address={
                    <>
                        {inbound.length} <Trans comment="Short of: inbound firewall rules">inbound</Trans>,{" "}
                        {outbound.length} <Trans comment="Short of: outbound firewall rules">outbound</Trans>
                    </>
                }
                location={location}
            />

            <div className={C.StandardBlockInfo}>
                <div className="text-muted">
                    <Trans comment="Short of: Number of resources">No. of resources</Trans>
                </div>
                {resources_assigned?.length ?? 0}
            </div>
        </StandardBlock>
    );
}

function NamePiece({ name, address, location }: { name: ReactNode; address: ReactNode; location?: ReactNode }) {
    return (
        <div className={cn(C.StandardBlockName, "text-ellipsis")}>
            <b className="text-ellipsis">{name}</b>
            <div className="text-ellipsis text-primary">
                {address}
                {!!location && <> / {location}</>}
            </div>
        </div>
    );
}
